// PendingApprovalScreen — full-bleed gate shown when me.role === 'pending'.
//
// AppShell renders this *instead of* the regular layout (no sidebar / no
// tabs) when the server says the user is awaiting approval. The user can
// hit "Check status" to refetch /api/me (in case an admin just approved
// them) or sign out.
//
// Language is whatever the user picked at onboarding — AppShell already
// passes the same `lang` it uses for the rest of the app.

import type { AccentTokens, ThemeTokens } from '@nayto/theme';
import { isRTL, type Lang } from '@nayto/i18n';
import { Icon } from '@/components/icons';
import { signOut, useMe } from '@/auth';

interface PendingApprovalScreenProps {
  theme: ThemeTokens;
  accent: AccentTokens;
  lang: Lang;
  t: (key: string) => string;
}

export function PendingApprovalScreen({ theme, accent, lang, t }: PendingApprovalScreenProps) {
  const rtl = isRTL(lang);
  const { refetch, me } = useMe();

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        height: '100vh',
        background: theme.bg,
        color: theme.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 420,
          width: '100%',
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 16,
          padding: 32,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: `${accent.main}22`,
            color: accent.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <Icon name="info" size={28} />
        </div>
        <h1
          style={{
            fontSize: 20,
            fontWeight: 700,
            margin: '0 0 12px',
            letterSpacing: -0.3,
          }}
        >
          {t('pendingTitle')}
        </h1>
        <p
          style={{
            fontSize: 14,
            color: theme.textMuted,
            lineHeight: 1.55,
            margin: '0 0 24px',
          }}
        >
          {t('pendingBody')}
        </p>
        {/* Which account is waiting — so the user can tell the admin
         * exactly what to approve. E-mail first; phone only for legacy
         * phone-auth accounts. */}
        {(me?.email || me?.phoneNumber) && (
          <div
            style={{
              fontSize: 11,
              color: theme.textSubtle,
              fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
              marginBottom: 20,
              direction: 'ltr',
            }}
          >
            {me?.email ?? me?.phoneNumber}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
          <button
            onClick={() => void refetch()}
            style={{
              padding: '10px 16px',
              borderRadius: 10,
              border: 'none',
              background: accent.main,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Icon name="refresh" size={14} />
            {t('pendingRefresh')}
          </button>
          <button
            onClick={() => void signOut()}
            style={{
              padding: '10px 16px',
              borderRadius: 10,
              border: `1px solid ${theme.border}`,
              background: 'transparent',
              color: theme.textMuted,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t('signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}
